import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import Swal from "sweetalert2";

import { uploadToBlob } from "../../lib/blobUpload";
import { COUNTRIES, STATES } from "../../lib/stateOptions";
import {
  formatPhone,
  isEmail,
  isPhone,
  isZipForCountry,
  normalizeWebsite,
  validateLogo,
} from "../../lib/validation";
import { useFormStore } from "../../store";
import type { SupplierFields } from "../../types";

// Subset of SupplierFields that Step 1 manages.
type Step1Fields = Pick<
  SupplierFields,
  | "new_name"
  | "new_addressline1"
  | "new_addressline2"
  | "new_city"
  | "new_stateprovince"
  | "new_zip"
  | "new_country"
  | "new_phone"
  | "new_fax"
  | "emailaddress"
  | "new_website"
  | "new_logourl"
>;

function openLogoModal(url: string) {
  if (!url) return;
  Swal.fire({
    imageUrl: url,
    imageAlt: "Company logo at full size",
    showConfirmButton: false,
    showCloseButton: true,
    width: "auto",
    background: "#fff",
  });
}

function pickStep1(supplier: SupplierFields): Step1Fields {
  const {
    new_name,
    new_addressline1,
    new_addressline2,
    new_city,
    new_stateprovince,
    new_zip,
    new_country,
    new_phone,
    new_fax,
    emailaddress,
    new_website,
    new_logourl,
  } = supplier;
  return {
    new_name,
    new_addressline1,
    new_addressline2,
    new_city,
    new_stateprovince,
    new_zip,
    new_country,
    new_phone,
    new_fax,
    emailaddress,
    new_website,
    new_logourl,
  };
}

export default function Step1Business() {
  const supplier = useFormStore((s) => s.supplier);
  const setSupplier = useFormStore((s) => s.setSupplier);
  const supplierId = supplier.id;
  // Existing-supplier check uses the frozen prefill snapshot so the field's
  // read-only state can't be flipped by editing supplier.id locally.
  const isExistingSupplier = useFormStore((s) => s.originalSupplier.id) != null;

  const {
    register,
    control,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<Step1Fields>({
    defaultValues: pickStep1(supplier),
    mode: "onBlur",
  });

  // Re-seed the form when a fresh prefill arrives (id changes from null → guid).
  useEffect(() => {
    reset(pickStep1(supplier));
  }, [supplierId, reset]); // eslint-disable-line react-hooks/exhaustive-deps

  // Push edits back to the store as the user types, scoped to Step 1 fields.
  useEffect(() => {
    const sub = watch((values) => setSupplier(values as Partial<SupplierFields>));
    return () => sub.unsubscribe();
  }, [watch, setSupplier]);

  const country = watch("new_country");
  const filteredStates = useMemo(() => STATES.filter((s) => s.country === country), [country]);

  // Local upload UI state. localPreview is a `blob:` URL good for immediate
  // display of the just-uploaded file (since the staging blob itself is
  // private and not directly readable until after submit + copy).
  const [logoUpload, setLogoUpload] = useState<{
    status: "idle" | "uploading" | "error";
    message?: string;
    localPreview?: string;
  }>({ status: "idle" });

  // Release the object URL when component unmounts or a new one replaces it.
  useEffect(() => {
    return () => {
      if (logoUpload.localPreview) URL.revokeObjectURL(logoUpload.localPreview);
    };
  }, [logoUpload.localPreview]);

  const handleLogoSelect = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const err = validateLogo(file);
    if (err) {
      setLogoUpload({ status: "error", message: err });
      return;
    }

    setLogoUpload({ status: "uploading" });
    try {
      const { url } = await uploadToBlob(file, "logo");
      const localPreview = URL.createObjectURL(file);
      setLogoUpload({ status: "idle", localPreview });
      setSupplier({ new_logourl: url });
    } catch (uploadError) {
      const message = uploadError instanceof Error ? uploadError.message : "Upload failed";
      setLogoUpload({ status: "error", message });
    }
  };

  const previewSrc = logoUpload.localPreview ?? supplier.new_logourl ?? undefined;

  return (
    <div>
      <div className="row g-4">
        {/* ---------- Left column: Business Details ---------- */}
        <div className="col-lg-6">
          <h3 className="brand-section-heading brand-section-heading--sub">
            <i className="fa-solid fa-user" aria-hidden="true" />
            Business Details
          </h3>

          <div className="mb-3">
            <label htmlFor="new_name" className="form-label">
              Company Name <span className="text-danger" aria-hidden="true">*</span>
            </label>
            <div className="input-group has-validation">
              <span className="input-group-text" aria-hidden="true">
                <i className="fa-solid fa-building" />
              </span>
              <input
                id="new_name"
                type="text"
                className={`form-control ${errors.new_name ? "is-invalid" : ""}`}
                aria-required="true"
                aria-invalid={errors.new_name ? "true" : undefined}
                aria-describedby={errors.new_name ? "new_name-err" : undefined}
                readOnly={isExistingSupplier}
                {...register("new_name", { required: "Company Name is required" })}
              />
              {errors.new_name && (
                <div id="new_name-err" className="invalid-feedback" aria-live="polite">
                  {errors.new_name.message}
                </div>
              )}
            </div>
          </div>

          <div className="mb-3">
            <label htmlFor="new_phone" className="form-label">Business Phone</label>
            <div className="input-group has-validation">
              <span className="input-group-text" aria-hidden="true">
                <i className="fa-solid fa-phone" />
              </span>
              <Controller
                name="new_phone"
                control={control}
                rules={{ validate: (v) => !v || isPhone(v) || "Use format (000) 000-0000" }}
                render={({ field, fieldState }) => (
                  <input
                    id="new_phone"
                    type="tel"
                    placeholder="(000) 000-0000"
                    className={`form-control ${fieldState.error ? "is-invalid" : ""}`}
                    aria-invalid={fieldState.error ? "true" : undefined}
                    aria-describedby={fieldState.error ? "new_phone-err" : undefined}
                    value={formatPhone(field.value)}
                    onChange={(e) => field.onChange(formatPhone(e.target.value))}
                    onBlur={field.onBlur}
                  />
                )}
              />
              {errors.new_phone && (
                <div id="new_phone-err" className="invalid-feedback" aria-live="polite">
                  {errors.new_phone.message}
                </div>
              )}
            </div>
          </div>

          <div className="mb-3">
            <label htmlFor="new_fax" className="form-label">Business Fax</label>
            <div className="input-group">
              <span className="input-group-text" aria-hidden="true">
                <i className="fa-solid fa-fax" />
              </span>
              <Controller
                name="new_fax"
                control={control}
                rules={{ validate: (v) => !v || isPhone(v) || "Use format (000) 000-0000" }}
                render={({ field, fieldState }) => (
                  <input
                    id="new_fax"
                    type="tel"
                    placeholder="(000) 000-0000"
                    className={`form-control ${fieldState.error ? "is-invalid" : ""}`}
                    aria-invalid={fieldState.error ? "true" : undefined}
                    value={formatPhone(field.value)}
                    onChange={(e) => field.onChange(formatPhone(e.target.value))}
                    onBlur={field.onBlur}
                  />
                )}
              />
            </div>
          </div>

          <div className="mb-3">
            <label htmlFor="emailaddress" className="form-label">
              Business Email <span className="text-danger" aria-hidden="true">*</span>
            </label>
            <div className="input-group has-validation">
              <span className="input-group-text" aria-hidden="true">
                <i className="fa-solid fa-envelope" />
              </span>
              <input
                id="emailaddress"
                type="email"
                placeholder="contact@example.com"
                className={`form-control ${errors.emailaddress ? "is-invalid" : ""}`}
                aria-required="true"
                aria-invalid={errors.emailaddress ? "true" : undefined}
                aria-describedby={errors.emailaddress ? "emailaddress-err" : undefined}
                {...register("emailaddress", {
                  required: "Business Email is required",
                  validate: (v) => !v || isEmail(v) || "Enter a valid email address",
                })}
              />
              {errors.emailaddress && (
                <div id="emailaddress-err" className="invalid-feedback" aria-live="polite">
                  {errors.emailaddress.message}
                </div>
              )}
            </div>
          </div>

          <div className="mb-3">
            <label htmlFor="new_website" className="form-label">Business Website</label>
            <div className="input-group">
              <span className="input-group-text" aria-hidden="true">
                <i className="fa-solid fa-globe" />
              </span>
              <input
                id="new_website"
                type="url"
                placeholder="https://example.com"
                className="form-control"
                {...register("new_website", {
                  onBlur: (e) => {
                    const normalized = normalizeWebsite(e.target.value);
                    if (normalized !== e.target.value) {
                      setValue("new_website", normalized, { shouldDirty: true });
                    }
                  },
                })}
              />
            </div>
          </div>
        </div>

        {/* ---------- Right column: Business Address ---------- */}
        <div className="col-lg-6">
          <h3 className="brand-section-heading brand-section-heading--sub">
            <i className="fa-solid fa-location-dot" aria-hidden="true" />
            Business Address
          </h3>

          <div className="mb-3">
            <label htmlFor="new_addressline1" className="form-label">Address Line 1</label>
            <div className="input-group">
              <span className="input-group-text" aria-hidden="true">
                <i className="fa-solid fa-location-dot" />
              </span>
              <input
                id="new_addressline1"
                type="text"
                className="form-control"
                {...register("new_addressline1")}
              />
            </div>
          </div>

          <div className="mb-3">
            <label htmlFor="new_addressline2" className="form-label">Address Line 2</label>
            <div className="input-group">
              <span className="input-group-text" aria-hidden="true">
                <i className="fa-solid fa-location-dot" />
              </span>
              <input
                id="new_addressline2"
                type="text"
                className="form-control"
                {...register("new_addressline2")}
              />
            </div>
          </div>

          <div className="row g-3">
            <div className="col-md-6 mb-3">
              <label htmlFor="new_city" className="form-label">City</label>
              <div className="input-group">
                <span className="input-group-text" aria-hidden="true">
                  <i className="fa-solid fa-city" />
                </span>
                <input
                  id="new_city"
                  type="text"
                  className="form-control"
                  {...register("new_city")}
                />
              </div>
            </div>

            <div className="col-md-6 mb-3">
              <label htmlFor="new_country" className="form-label">Country</label>
              <div className="input-group">
                <span className="input-group-text" aria-hidden="true">
                  <i className="fa-solid fa-flag" />
                </span>
                <select
                  id="new_country"
                  className="form-select"
                  {...register("new_country")}
                >
                  <option value="">— Select —</option>
                  {COUNTRIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="col-md-6 mb-3">
              <label htmlFor="new_stateprovince" className="form-label">State / Province</label>
              <div className="input-group">
                <span className="input-group-text" aria-hidden="true">
                  <i className="fa-solid fa-map" />
                </span>
                <select
                  id="new_stateprovince"
                  className="form-select"
                  disabled={!country}
                  {...register("new_stateprovince", {
                    setValueAs: (v) => (v === "" || v == null ? null : Number(v)),
                  })}
                >
                  <option value="">{country ? "— Select —" : "Pick a country first"}</option>
                  {filteredStates.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="col-md-6 mb-3">
              <label htmlFor="new_zip" className="form-label">ZIP / Postal Code</label>
              <div className="input-group has-validation">
                <span className="input-group-text" aria-hidden="true">
                  <i className="fa-solid fa-hashtag" />
                </span>
                <input
                  id="new_zip"
                  type="text"
                  className={`form-control ${errors.new_zip ? "is-invalid" : ""}`}
                  aria-invalid={errors.new_zip ? "true" : undefined}
                  aria-describedby={errors.new_zip ? "new_zip-err" : undefined}
                  {...register("new_zip", {
                    validate: (v) =>
                      !v || isZipForCountry(v, country ?? undefined) ||
                      (country === "Canada"
                        ? "Use Canadian postal format A1A 1A1"
                        : "Use 5-digit ZIP, optionally ZIP+4"),
                  })}
                />
                {errors.new_zip && (
                  <div id="new_zip-err" className="invalid-feedback" aria-live="polite">
                    {errors.new_zip.message}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ---------- Logo row ---------- */}
      <div className="row g-4 mt-1">
        <div className="col-12">
          <h3 className="brand-section-heading brand-section-heading--sub">
            <i className="fa-solid fa-image" aria-hidden="true" />
            Logo
          </h3>
          <div className="row g-3 align-items-start">
            {/* Left column — upload controls + status text */}
            <div className="col-md-7">
              <div className="input-group has-validation">
                <label htmlFor="logo-file" className="input-group-text" aria-hidden="true">
                  <i className="fa-solid fa-cloud-arrow-up" />
                </label>
                <input
                  id="logo-file"
                  type="file"
                  accept="image/png,image/jpeg"
                  disabled={logoUpload.status === "uploading"}
                  className={`form-control ${logoUpload.status === "error" ? "is-invalid" : ""}`}
                  aria-describedby="logo-help"
                  onChange={handleLogoSelect}
                />
              </div>
              {logoUpload.status === "uploading" && (
                <div className="form-text text-primary" aria-live="polite">
                  <i className="fa-solid fa-spinner fa-spin me-1" aria-hidden="true" />
                  Uploading…
                </div>
              )}
              {logoUpload.status === "error" && (
                <div className="invalid-feedback d-block" aria-live="polite">
                  {logoUpload.message}
                </div>
              )}
              {logoUpload.status === "idle" && logoUpload.localPreview && (
                <div className="form-text text-success" aria-live="polite">
                  <i className="fa-solid fa-circle-check me-1" aria-hidden="true" />
                  New logo staged. It replaces the existing one when you submit.
                </div>
              )}
              <small id="logo-help" className="form-text text-muted d-block">
                JPG or PNG up to 10 MB.
              </small>
            </div>

            {/* Right column — current logo preview (click to enlarge) */}
            <div className="col-md-5">
              {previewSrc ? (
                <div className="d-flex flex-column align-items-start gap-1">
                  <button
                    type="button"
                    className="btn btn-link p-0 border rounded"
                    onClick={() => openLogoModal(previewSrc)}
                    aria-label="View logo larger"
                    title="Click to view larger"
                  >
                    <img
                      src={previewSrc}
                      alt="Current company logo (click to enlarge)"
                      style={{ maxHeight: 96, maxWidth: 200, objectFit: "contain", cursor: "zoom-in" }}
                      className="p-1"
                    />
                  </button>
                  <small className="text-muted">
                    <i className="fa-solid fa-magnifying-glass-plus me-1" aria-hidden="true" />
                    Click to view larger
                  </small>
                </div>
              ) : (
                <div className="text-muted small">No logo on file yet.</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
